import { useSelector } from "react-redux";
import { Store } from "../store/store";
import { UserSelectionState } from "./userSelection";
import { GeneralState } from "./general";
import { AuthorizationState } from "./auth";
import { ApiCallState } from "../api-model/api";
import { UserDto } from "../api-model/general/auth";
import { TFunction, useTranslation } from "react-i18next";
import { WorkContractDto } from "../api-model/employees/workContract";

export const useGeneralState = (): GeneralState => {
  return useSelector<Store, GeneralState>((state) => state.general);
};

export const useLocale = (): string | undefined => {
  return useSelector<Store, string | undefined>(
    (state) => state.general?.self?.data?.settings?.locale
  );
};

export const useCurrentLocale = (): string => {
  return useSelector<Store, string>(
    (state) =>
      state.general.self.data.settings.locale?.replace("_", "-") ?? "en-GB"
  );
};

export const useGeneralAuth = (): AuthorizationState => {
  return useSelector<Store, AuthorizationState>((state) => state.general.auth);
};

export const useUserSelection = (): UserSelectionState => {
  return useSelector<Store, UserSelectionState>(
    (state) => state.general.userSelection
  );
};

export const useMobileVersion = (): boolean => {
  return useSelector<Store, boolean>((state) => state.general.mobileVersion);
};
export const useAuthenticationByCredentialsRequest = (): ApiCallState => {
  return useSelector<Store, ApiCallState>(
    (state) => state.general.auth.loginByCredentials
  );
};

export const useAuthenticationBySessionRequest = (): ApiCallState => {
  return useSelector<Store, ApiCallState>(
    (state) => state.general.auth.loginBySession
  );
};

export const useAuthentication = (): boolean => {
  return useSelector<Store, boolean>(
    (state) => state.general.auth.authentication
  );
};

export const useSelf = (): {
  loading: boolean;
  data: UserDto | Record<string, never>;
} => {
  return useSelector<
    Store,
    { loading: boolean; data: UserDto | Record<string, never> }
  >((state) => state.general.self);
};

export const useSelfWorkContracts = (): WorkContractDto[] => {
  return useSelector<Store, WorkContractDto[]>(
    (state) => state.general.selfWorkContracts
  );
};

export type AllEnumTranslations = {
  abrechnungsGruppe: TFunction<"abrechnungs-gruppe", undefined>;
  schulabschluss: TFunction<"schulabschluss", undefined>;
  ausbildungsabschluss: TFunction<"ausbildungsabschluss", undefined>;
  vertragsForm: TFunction<"vertragsform", undefined>;
  arbeitnehmerTyp: TFunction<"arbeitnehmer-typen", undefined>;
  trueFalse: TFunction<"true-false", undefined>;
  sex: TFunction<"sex", undefined>;
  employee: TFunction<"employee", undefined>;
};

export const useAllEnumTranslations = (): AllEnumTranslations => {
  const abrechnungsGruppe = useTranslation("abrechnungs-gruppe").t;

  const schulabschluss = useTranslation("schulabschluss").t;
  const ausbildungsabschluss = useTranslation("ausbildungsabschluss").t;
  const vertragsForm = useTranslation("vertragsform").t;

  const arbeitnehmerTyp = useTranslation("arbeitnehmer-typen").t;

  const trueFalse = useTranslation("true-false").t;

  const sex = useTranslation("sex").t;
  const employee = useTranslation("employees").t;

  return {
    abrechnungsGruppe,
    schulabschluss,
    ausbildungsabschluss,
    vertragsForm,
    arbeitnehmerTyp,
    trueFalse,
    sex,
    employee,
  };
};
